import React from 'react';
import { Helmet } from 'react-helmet';

const AIContext = () => {
  const contextData = {
    platform: {
      name: 'LaunchPad / Versa Launch',
      tagline: 'Launch your local business to the next level',
      description: 'An all-in-one platform that helps local service businesses (plumbing, HVAC, electrical, cleaning, etc.) increase visibility, generate leads, and automate customer interactions using AI-powered tools.',
      mission: 'Empower local business owners with enterprise-grade automation and marketing tools without the enterprise price tag.',
      website: 'https://versalaunch.com',
      targetAudience: 'Local service business owners, contractors, independent professionals in trades industries'
    },

    coreServices: [
      {
        name: 'AI Phone Assistant',
        description: 'Intelligent AI-powered phone system that answers customer calls 24/7, qualifies leads, and schedules appointments automatically.',
        benefits: [
          'Never miss a lead - AI answers every call',
          'Qualify leads automatically',
          'Schedule appointments instantly',
          'Available 24/7 coverage for nights and weekends',
          'Integrate with existing CRM and calendar'
        ],
        features: [
          'Natural language understanding',
          'Multi-language support',
          'Smart lead qualification',
          'Real-time notifications',
          'Call recording and transcription',
          'Integration with popular services (Calendly, Square, etc.)'
        ]
      },
      {
        name: 'Local Search Optimization',
        description: 'Advanced tools to dominate local Google Search and Maps rankings for your service area.',
        benefits: [
          'Appear first in local search results',
          'Increase visibility to customers actively searching for your services',
          'Get more clicks and inbound leads from Google',
          'Outrank competitors in your area'
        ],
        features: [
          'Local SEO optimization',
          'Google Business Profile management',
          'Review management system',
          'Citation building',
          'Local keyword targeting'
        ]
      },
      {
        name: 'Client Portals',
        description: 'Branded customer portals for appointment booking, payments, and customer communication.',
        benefits: [
          'Allow customers to book appointments online 24/7',
          'Reduce no-shows with automated reminders',
          'Collect payments before or after service',
          'Improve customer communication'
        ],
        features: [
          'Branded booking system',
          'Automated reminders',
          'Online payment collection',
          'Customer history and notes',
          'Service request tracking'
        ]
      },
      {
        name: 'Competition Report',
        description: 'AI-powered market analysis showing how you rank against competitors and opportunities for growth.',
        benefits: [
          'See exactly how you compare to competitors',
          'Identify market gaps and opportunities',
          'Understand what\'s working (and what\'s not)',
          'Data-driven insights for strategy'
        ],
        features: [
          'Competitor ranking analysis',
          'Review comparison',
          'Market gap identification',
          'Actionable recommendations',
          'Real-time tracking'
        ]
      },
      {
        name: 'Business Phone System',
        description: 'Professional business phone number and call management system.',
        benefits: [
          'Professional business phone number',
          'Call routing and forwarding',
          'Call recording and analytics',
          'Team collaboration features'
        ],
        features: [
          'Local business phone number',
          'Call recording',
          'Call routing',
          'Call notes and logging',
          'Multiple user support'
        ]
      },
      {
        name: 'Web Apps & Custom Design',
        description: 'Beautiful, conversion-optimized websites and web applications that turn visitors into customers.',
        benefits: [
          'Professional online presence',
          'Mobile-responsive design',
          'SEO-optimized pages',
          'Conversion-focused layouts'
        ],
        features: [
          'Custom website design',
          'Mobile optimization',
          'SEO integration',
          'Contact forms',
          'Service showcases'
        ]
      },
      {
        name: 'Square Integrations',
        description: 'Deep integrations with Square POS, payroll, and other business tools.',
        benefits: [
          'Seamless payment processing',
          'Unified business data across platforms',
          'Automated payroll and accounting'
        ],
        features: [
          'Payment processing integration',
          'POS system sync',
          'Inventory management',
          'Payroll integration'
        ]
      },
      {
        name: 'Marketing Automation',
        description: 'Automated email, SMS, and social media marketing campaigns.',
        benefits: [
          'Stay top-of-mind with customers',
          'Generate repeat business',
          'Low-touch customer follow-up',
          'Increase customer lifetime value'
        ],
        features: [
          'Email marketing automation',
          'SMS campaigns',
          'Social media scheduling',
          'Customer segmentation',
          'Campaign analytics'
        ]
      }
    ],

    keyFeatures: [
      {
        number: '1',
        title: 'More Google Visibility',
        description: 'Dominate local search results and get found by customers actively looking for your services.'
      },
      {
        number: '2',
        title: 'More Online Leads',
        description: 'Capture potential customers through multiple channels: search, maps, web, social media.'
      },
      {
        number: '3',
        title: 'More Repeat Jobs',
        description: 'Build lasting customer relationships and generate recurring revenue from satisfied clients.'
      },
      {
        number: '4',
        title: 'More Business',
        description: 'All-in-one platform to run and grow your business with automation and intelligence.'
      }
    ],

    benefits: [
      {
        title: 'Never Miss a Lead',
        description: '24/7 AI phone assistant answers every call and schedules appointments automatically.'
      },
      {
        title: 'Increase Revenue',
        description: 'More leads + better conversion = bigger bottom line. Average clients see 65%+ more leads.'
      },
      {
        title: 'Save Time',
        description: 'Automate appointment scheduling, lead qualification, and customer follow-ups.'
      },
      {
        title: 'Compete with Big Guys',
        description: 'Enterprise-level tools at a fraction of the cost. Stay competitive without breaking the bank.'
      },
      {
        title: 'Stand Out',
        description: 'Professional online presence with mobile apps, custom websites, and marketing automation.'
      },
      {
        title: 'Grow Smart',
        description: 'Data-driven insights into what\'s working. See exactly how you compare to competitors.'
      }
    ],

    impactStats: [
      { value: '+65%', label: 'More local leads' },
      { value: '24/7', label: 'AI phone coverage' },
      { value: '#1', label: 'Search position goal' },
      { value: '+200%', label: 'Average lead increase (in case studies)' },
      { value: '100+', label: 'Local businesses using platform' }
    ],

    caseStudies: [
      {
        name: 'Maria Santos',
        role: 'Barista & Cafe Owner',
        headline: 'Tripled morning rush bookings',
        stat: '+200%',
        sub: 'more bookings',
        story: 'Implemented AI phone assistant and online booking system. Morning rush reservations increased dramatically.'
      },
      {
        name: 'James Anderson',
        role: 'Clothing Company Owner',
        headline: 'Grew customer base significantly',
        stat: '+85%',
        sub: 'new customers',
        story: 'Used local SEO optimization and marketing automation to reach more customers.'
      },
      {
        name: 'Patricia Williams',
        role: 'Notary Services',
        headline: 'Doubled client appointments',
        stat: '2X',
        sub: 'appointments',
        story: 'AI phone system eliminated missed calls and automated appointment scheduling.'
      },
      {
        name: 'Shaye',
        role: 'Pottery Shop Owner',
        headline: 'Increased workshop enrollment',
        stat: '+120%',
        sub: 'revenue growth',
        story: 'Combined website improvements with email marketing automation for consistent enrollments.'
      },
      {
        name: 'Lisa Martinez',
        role: 'Sweet Treats Bakery',
        headline: 'Online orders up monthly',
        stat: '+$3,500/mo',
        sub: 'online sales',
        story: 'Web app integration and online ordering system transformed business model.'
      },
      {
        name: 'Sevent Tree Farm',
        role: 'Tree Farm & Seasonal Market',
        headline: 'Booked more seasonal customers online',
        stat: '+72%',
        sub: 'more inquiries',
        story: 'Local search optimization and client portal increased seasonal bookings.'
      }
    ],

    pricingTiers: [
      {
        name: 'Starter',
        price: 'Contact for pricing',
        description: 'Perfect for solo entrepreneurs or small teams getting started.',
        features: [
          'AI Phone Assistant (basic)',
          'Client Portal',
          'Basic Local SEO',
          'Email support'
        ]
      },
      {
        name: 'Professional',
        price: 'Contact for pricing',
        description: 'For established businesses wanting to scale efficiently.',
        features: [
          'AI Phone Assistant (advanced)',
          'Full Client Portal',
          'Advanced Local SEO',
          'Competitor Reports',
          'Marketing Automation',
          'Priority support'
        ]
      },
      {
        name: 'Enterprise',
        price: 'Custom',
        description: 'Fully customized solutions for multi-location or large teams.',
        features: [
          'Everything in Professional',
          'Custom Website/App',
          'Dedicated Account Manager',
          'Custom Integrations',
          'Advanced Reporting',
          '24/7 Premium Support'
        ]
      }
    ],

    industries: [
      'Plumbing',
      'HVAC',
      'Electrical',
      'Painting',
      'Landscaping',
      'Cleaning Services',
      'Roofing',
      'Appliance Repair',
      'Carpet Cleaning',
      'Pest Control',
      'Tree Services',
      'Handyman Services',
      'General Contracting',
      'Auto Repair',
      'Salon & Spa',
      'Dental',
      'Medical',
      'Home Services',
      'Professional Services'
    ],

    integrations: [
      'Google Business Profile',
      'Google Maps',
      'Square POS',
      'Calendly',
      'Zapier',
      'Gmail',
      'Stripe',
      'PayPal',
      'Facebook',
      'Instagram',
      'Yelp'
    ],

    company: {
      mission: 'Democratize business tools for local entrepreneurs',
      vision: 'Make enterprise automation accessible to every business owner',
      founded: '2023',
      team: 'Founding team with expertise in AI, business automation, and local services',
      values: [
        'Customer Success',
        'Innovation',
        'Simplicity',
        'Ethics',
        'Accessibility'
      ]
    },

    support: {
      email: 'contact@versalaunch.com',
      phone: 'Available 24/7',
      channels: ['Email', 'Chat', 'Phone', 'Video Support'],
      resources: [
        'Knowledge Base',
        'Video Tutorials',
        'Webinars',
        'Case Studies',
        'Best Practices Guide',
        'Blog & Resources'
      ]
    },

    security: {
      certifications: ['SOC 2 Compliant', 'GDPR Compliant', 'CCPA Compliant'],
      dataProtection: 'Enterprise-grade encryption, secure servers, regular security audits',
      privacy: 'Your business data is yours. We never sell customer information.'
    }
  };

  return (
    <>
      <Helmet>
        <title>AI Context - Versa Launch Platform Information</title>
        <meta name="robots" content="all" />
        <meta name="description" content="Complete platform information for Versa Launch - AI-powered local business tools" />
      </Helmet>

      <main style={{ padding: '40px 20px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <h1 style={{ fontSize: '2.5rem', marginBottom: '10px' }}>Versa Launch - Complete Platform Guide</h1>
        <p style={{ fontSize: '1.1rem', color: '#666', marginBottom: '40px', lineHeight: '1.6' }}>
          This page contains comprehensive information about the Versa Launch platform for AI assistants, crawlers, and researchers.
        </p>

        {/* Platform Overview */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ fontSize: '2rem', marginBottom: '20px', borderBottom: '2px solid #d4af37', paddingBottom: '10px' }}>Platform Overview</h2>
          <div style={{ background: '#f5f5f5', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
            <p><strong>Name:</strong> {contextData.platform.name}</p>
            <p><strong>Tagline:</strong> {contextData.platform.tagline}</p>
            <p><strong>Mission:</strong> {contextData.platform.mission}</p>
            <p><strong>Target Audience:</strong> {contextData.platform.targetAudience}</p>
            <p><strong>Website:</strong> {contextData.platform.website}</p>
          </div>
          <p style={{ lineHeight: '1.8', color: '#333', fontSize: '1rem' }}>
            {contextData.platform.description}
          </p>
        </section>

        {/* Core Services */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ fontSize: '2rem', marginBottom: '30px', borderBottom: '2px solid #d4af37', paddingBottom: '10px' }}>Core Services & Features</h2>
          {contextData.coreServices.map((service, idx) => (
            <div key={idx} style={{ marginBottom: '40px', background: '#fafafa', padding: '20px', borderRadius: '8px', borderLeft: '4px solid #d4af37' }}>
              <h3 style={{ fontSize: '1.5rem', color: '#0a0a0a', marginTop: 0 }}>{service.name}</h3>
              <p style={{ color: '#555', fontSize: '1rem', lineHeight: '1.6' }}>{service.description}</p>
              
              <div style={{ marginTop: '15px' }}>
                <strong>Key Benefits:</strong>
                <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                  {service.benefits.map((benefit, i) => (
                    <li key={i} style={{ marginBottom: '6px', color: '#333' }}>{benefit}</li>
                  ))}
                </ul>
              </div>

              <div style={{ marginTop: '15px' }}>
                <strong>Features:</strong>
                <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                  {service.features.map((feature, i) => (
                    <li key={i} style={{ marginBottom: '6px', color: '#333' }}>{feature}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </section>

        {/* Impact & Stats */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ fontSize: '2rem', marginBottom: '20px', borderBottom: '2px solid #d4af37', paddingBottom: '10px' }}>Business Impact & Statistics</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
            {contextData.impactStats.map((stat, idx) => (
              <div key={idx} style={{ background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1), rgba(212, 175, 55, 0.05))', padding: '20px', borderRadius: '8px', textAlign: 'center', border: '1px solid rgba(212, 175, 55, 0.3)' }}>
                <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#d4af37', marginBottom: '10px' }}>{stat.value}</div>
                <div style={{ fontSize: '1rem', color: '#555' }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Case Studies */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ fontSize: '2rem', marginBottom: '30px', borderBottom: '2px solid #d4af37', paddingBottom: '10px' }}>Real Customer Success Stories</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            {contextData.caseStudies.map((study, idx) => (
              <div key={idx} style={{ background: '#fff', padding: '20px', borderRadius: '8px', border: '1px solid #ddd' }}>
                <h4 style={{ marginTop: 0, color: '#0a0a0a' }}>{study.name}</h4>
                <p style={{ color: '#d4af37', fontWeight: 'bold', margin: '5px 0' }}>{study.role}</p>
                <p style={{ color: '#555', fontSize: '0.95rem' }}>{study.headline}</p>
                <div style={{ background: '#f9f9f9', padding: '10px', borderRadius: '4px', margin: '10px 0' }}>
                  <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#d4af37' }}>{study.stat}</div>
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>{study.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Pricing */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ fontSize: '2rem', marginBottom: '30px', borderBottom: '2px solid #d4af37', paddingBottom: '10px' }}>Pricing Plans</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
            {contextData.pricingTiers.map((tier, idx) => (
              <div key={idx} style={{ background: '#f9f9f9', padding: '25px', borderRadius: '8px', border: '1px solid #ddd' }}>
                <h3 style={{ marginTop: 0, fontSize: '1.3rem' }}>{tier.name}</h3>
                <p style={{ fontSize: '1.1rem', color: '#d4af37', fontWeight: 'bold', margin: '10px 0' }}>{tier.price}</p>
                <p style={{ color: '#666', fontSize: '0.95rem', marginBottom: '15px' }}>{tier.description}</p>
                <ul style={{ paddingLeft: '20px', margin: 0 }}>
                  {tier.features.map((feature, i) => (
                    <li key={i} style={{ marginBottom: '8px', color: '#333' }}>{feature}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Industries Served */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ fontSize: '2rem', marginBottom: '20px', borderBottom: '2px solid #d4af37', paddingBottom: '10px' }}>Industries & Use Cases</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {contextData.industries.map((industry, idx) => (
              <span key={idx} style={{ background: '#d4af37', color: '#fff', padding: '8px 16px', borderRadius: '20px', fontSize: '0.9rem', fontWeight: '600' }}>
                {industry}
              </span>
            ))}
          </div>
        </section>

        {/* Integrations */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ fontSize: '2rem', marginBottom: '20px', borderBottom: '2px solid #d4af37', paddingBottom: '10px' }}>Platform Integrations</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {contextData.integrations.map((integration, idx) => (
              <span key={idx} style={{ background: '#e8e8e8', color: '#333', padding: '8px 14px', borderRadius: '6px', fontSize: '0.9rem' }}>
                ✓ {integration}
              </span>
            ))}
          </div>
        </section>

        {/* Company Info */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ fontSize: '2rem', marginBottom: '20px', borderBottom: '2px solid #d4af37', paddingBottom: '10px' }}>About the Company</h2>
          <div style={{ background: '#f5f5f5', padding: '20px', borderRadius: '8px' }}>
            <p><strong>Mission:</strong> {contextData.company.mission}</p>
            <p><strong>Vision:</strong> {contextData.company.vision}</p>
            <p><strong>Founded:</strong> {contextData.company.founded}</p>
            <p><strong>Team:</strong> {contextData.company.team}</p>
            <p><strong>Core Values:</strong></p>
            <ul style={{ paddingLeft: '20px' }}>
              {contextData.company.values.map((value, idx) => (
                <li key={idx}>{value}</li>
              ))}
            </ul>
          </div>
        </section>

        {/* Support */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ fontSize: '2rem', marginBottom: '20px', borderBottom: '2px solid #d4af37', paddingBottom: '10px' }}>Customer Support</h2>
          <div style={{ background: '#f5f5f5', padding: '20px', borderRadius: '8px' }}>
            <p><strong>Email:</strong> {contextData.support.email}</p>
            <p><strong>Phone:</strong> {contextData.support.phone}</p>
            <p><strong>Support Channels:</strong> {contextData.support.channels.join(', ')}</p>
            <p><strong>Available Resources:</strong></p>
            <ul style={{ paddingLeft: '20px' }}>
              {contextData.support.resources.map((resource, idx) => (
                <li key={idx}>{resource}</li>
              ))}
            </ul>
          </div>
        </section>

        {/* Security & Compliance */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ fontSize: '2rem', marginBottom: '20px', borderBottom: '2px solid #d4af37', paddingBottom: '10px' }}>Security & Compliance</h2>
          <div style={{ background: '#f5f5f5', padding: '20px', borderRadius: '8px' }}>
            <p><strong>Certifications:</strong> {contextData.security.certifications.join(', ')}</p>
            <p><strong>Data Protection:</strong> {contextData.security.dataProtection}</p>
            <p><strong>Privacy Commitment:</strong> {contextData.security.privacy}</p>
          </div>
        </section>

        {/* Raw JSON for Parsing */}
        <section style={{ marginBottom: '60px', background: '#f0f0f0', padding: '20px', borderRadius: '8px', marginTop: '80px' }}>
          <h2 style={{ marginTop: 0 }}>Structured Data (JSON)</h2>
          <p style={{ fontSize: '0.9rem', color: '#666' }}>For programmatic access to platform information:</p>
          <pre style={{ background: '#fff', padding: '15px', borderRadius: '4px', overflow: 'auto', fontSize: '0.85rem' }}>
            {JSON.stringify(contextData, null, 2)}
          </pre>
        </section>
      </main>
    </>
  );
};

export default AIContext;
